import logger from "./utils/logger";
import config from "./config";
import { isIngestUrl, setCorrelationId } from "./utils/remoteLog";

interface FetchOptions extends Omit<RequestInit, "headers"> {
  headers?: Record<string, string>;
  logToUI?: boolean;
  uiLogMessage?: string | null | undefined;
  forwardToBackend?: boolean;
  patientId?: string | null;
  skipLogoutOn401?: boolean;
}

export interface ApiResponse {
  result?: unknown;
  [key: string]: unknown;
  error?: string;
  blue_patient_id?: string | number | null;
}

export function isApiError(
  resp: unknown,
): resp is { error: string; status?: number } {
  return typeof resp === "object" && resp !== null && "error" in resp;
}

/**
 * A UUID that works outside a secure context.
 *
 * `crypto.randomUUID` is only defined in a **secure context** (HTTPS or
 * `localhost`) — over a plain-HTTP LAN origin (e.g. local dev reached by WSL IP)
 * it is `undefined`, and calling it would throw and break the very first
 * request. Fall back to a v4 UUID built from `crypto.getRandomValues`, which
 * *is* available on insecure origins, so a correlation id can never hard-break
 * the app. Production is always HTTPS, so the fast path is used there.
 *
 * @returns A v4 UUID string.
 */
export function safeUUID(): string {
  if (typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 1
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

let currentSessionId: string | null = null;
let currentPatientId: string | null = null;

const DEV_IDENTITY_PARAM_KEYS = ["user_email", "user_id", "user_name"] as const;

export function applyDevIdentityParams(endpoint: string): string {
  if (config.AUTH_MODE !== "iap" || config.ENVIRONMENT === "production") {
    return endpoint;
  }
  if (typeof window === "undefined" || !window.location?.search) {
    return endpoint;
  }

  const browserParams = new URLSearchParams(window.location.search);
  const [path, existingQuery = ""] = endpoint.split("?");
  const merged = new URLSearchParams(existingQuery);

  for (const key of DEV_IDENTITY_PARAM_KEYS) {
    const value = browserParams.get(key);
    if (value !== null && !merged.has(key)) {
      merged.set(key, value);
    }
  }

  const queryString = merged.toString();
  return queryString ? `${path}?${queryString}` : path;
}

let handleLogout: () => void = () => {
  fetch("/api/auth/logout", { method: "POST" }).finally(() => {
    window.location.reload();
  });
};

/**
 * Per-attempt backoff (ms) for the transient-retry policy. The length is the
 * retry budget: index 0 is the wait before the 1st retry, etc.
 */
const RETRY_BACKOFF_MS = [200, 500] as const;

/** Sleep helper for retry backoff. */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Decide whether a failed attempt is a transient blip worth retrying.
 *
 * Only idempotent GETs retry, and only on a 503 or a network/exception error
 * (no HTTP status). A 4xx (client error) and every non-GET method (to avoid
 * double-applying a mutation) are never retried.
 *
 * @param method - The request method.
 * @param result - The attempt's result (data or an error envelope).
 * @returns True when the attempt should be retried.
 */
function isTransientRetryable(
  method: string,
  result: { error: string; status?: number } | unknown,
): boolean {
  if (method.toUpperCase() !== "GET" || !isApiError(result)) {
    return false;
  }
  // A 503 (service unavailable, e.g. momentary DB contention) is transient; a
  // missing status is a network/exception failure (also transient). Any other
  // status (4xx client errors, 500, etc.) is surfaced as-is.
  return result.status === undefined || result.status === 503;
}

const apiUtils = {
  setLogoutHook(hook: () => void) {
    handleLogout = hook;
  },

  setSessionId(sessionId: string) {
    currentSessionId = sessionId;
    // Share the session id with the remote-log transport so shipped client-log
    // entries correlate with backend request logs (ADR-009).
    setCorrelationId(sessionId);
    logger.devOnly.debug(`apiUtils: Session ID set to ${sessionId}`);
  },

  setPatientId(patientId: string | null) {
    currentPatientId = patientId;
    logger.devOnly.debug(`apiUtils: Patient ID set to ${patientId}`);
  },

  getPatientId(): string | null {
    return currentPatientId;
  },

  /**
   * Fetch with a bounded transient-failure retry.
   *
   * Issues the request, and on a transient failure of an idempotent GET (a 503
   * or a network/exception error) retries up to {@link RETRY_BACKOFF_MS}.length
   * times with short backoff, so a momentary DB-contention blip self-heals
   * rather than surfacing an "Unable to load". Mutations (POST/PATCH/DELETE) and
   * client errors (4xx) are never retried; after the budget is exhausted the
   * last error is returned unchanged.
   *
   * @typeParam T - The expected success payload type.
   * @param endpoint - The API path (relative to the backend URL).
   * @param method - The HTTP method (defaults to GET).
   * @param bodyPayload - Optional request body (JSON-stringified if not a string).
   * @param options - Fetch + logging/toast options.
   * @returns The parsed payload, or an error envelope.
   */
  async fetchData<T = ApiResponse>(
    endpoint: string,
    method: string = "GET",
    bodyPayload?: unknown,
    options: FetchOptions = {},
  ): Promise<T | { error: string; status?: number }> {
    let result = await this.attemptFetch<T>(
      endpoint,
      method,
      bodyPayload,
      options,
    );
    for (const backoffMs of RETRY_BACKOFF_MS) {
      if (!isTransientRetryable(method, result)) break;
      await delay(backoffMs);
      result = await this.attemptFetch<T>(
        endpoint,
        method,
        bodyPayload,
        options,
      );
    }
    return result;
  },

  /**
   * Perform a single fetch attempt (no retry).
   *
   * @typeParam T - The expected success payload type.
   * @param endpoint - The API path (relative to the backend URL).
   * @param method - The HTTP method.
   * @param bodyPayload - Optional request body.
   * @param options - Fetch + logging/toast options.
   * @returns The parsed payload, or an error envelope.
   */
  async attemptFetch<T = ApiResponse>(
    endpoint: string,
    method: string,
    bodyPayload: unknown,
    options: FetchOptions,
  ): Promise<T | { error: string; status?: number }> {
    const backendUrl = config.BACKEND_URL ?? "";
    const {
      headers: customHeaders,
      logToUI = false,
      uiLogMessage = null,
      forwardToBackend = true,
      ...otherOptions
    } = options;

    const requestEndpoint = applyDevIdentityParams(endpoint);

    // Self-recursion guard (ADR-009): a failed POST to the client-log ingest
    // must never itself ship a client-log, or a backend outage loops forever.
    // For the ingest endpoint we keep failure logs console-only.
    const shipFailures = forwardToBackend && !isIngestUrl(requestEndpoint);

    const headers: Record<string, string> = {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...customHeaders,
    };

    if (currentSessionId) {
      headers["X-Session-ID"] = currentSessionId;
    }

    const effectivePatientId = options.patientId ?? currentPatientId;

    if (effectivePatientId && !headers["X-Patient-ID"]) {
      headers["X-Patient-ID"] = effectivePatientId;
    }

    const correlationId = safeUUID();
    headers["X-Correlation-ID"] = correlationId;

    const requestBody =
      bodyPayload !== undefined ? bodyPayload : otherOptions.body;

    if (requestBody) {
      otherOptions.body =
        typeof requestBody === "string"
          ? requestBody
          : JSON.stringify(requestBody);
    } else {
      delete otherOptions.body;
    }

    const fetchOptions: RequestInit = {
      method: method,
      headers,
      ...otherOptions,
    };

    let toastId: string | undefined = undefined;

    const pathSegments = endpoint.split("/");
    const lastSegment = pathSegments.pop();
    const shortEndpoint = lastSegment ? lastSegment.split("?")[0] : endpoint;
    const baseLogMessage =
      uiLogMessage || `Fetching ${method} ${shortEndpoint}`;

    try {
      if (logToUI) {
        toastId = Math.random().toString(36).substring(7);
        logger.logToast(baseLogMessage, "in-progress", "", toastId);
      }
      logger.devOnly.debug("API Fetch Start", {
        method,
        url: `${backendUrl}${requestEndpoint}`,
        correlation_id: correlationId,
      });

      const response: Response = await fetch(
        `${backendUrl}${requestEndpoint}`,
        fetchOptions,
      );

      if (response.status === 204) {
        logger.devOnly.debug(
          `API Fetch Success (204 No Content): ${method} ${backendUrl}${requestEndpoint}`,
        );
        return {} as T;
      }

      if (!response.ok) {
        if (response.status === 401) {
          logger.warn("IAP session expired, redirecting to sign-in");
          if (!options.skipLogoutOn401) {
            handleLogout();
          }
          return { error: "IAP session expired", status: 401 };
        }

        let errorData: Record<string, unknown> = {};
        let errorMessage: string;
        try {
          errorData = (await response.json()) as Record<string, unknown>;
          const detailedMessage = errorData.detail;
          errorMessage = detailedMessage
            ? `API error! status: ${response.status} - ${detailedMessage}`
            : `API error! status: ${response.status} - ${response.statusText}`;
        } catch (parseError) {
          errorMessage = `API error! status: ${response.status} - ${response.statusText}. Failed to parse error response body.`;
          const errorToLog =
            parseError instanceof Error
              ? parseError
              : new Error(String(parseError));
          const logMessage = `API Fetch Failed: Could not parse error response body for ${method} ${backendUrl}${requestEndpoint}`;
          if (shipFailures) {
            logger.warn(logMessage, errorToLog);
          } else {
            logger.local.warn(logMessage, errorToLog);
          }
        }

        if (toastId && logToUI) {
          logger.updateToast(
            toastId,
            baseLogMessage,
            "failure",
            `Status ${response.status}`,
          );
        } else if (logToUI) {
          logger.logToast(
            baseLogMessage,
            "failure",
            `Status ${response.status}`,
          );
        }
        const logMessage = `API Fetch Failed: ${method} ${backendUrl}${requestEndpoint}`;
        const metadata = {
          status: response.status,
          statusText: response.statusText,
          responseBody: errorData,
        };
        if (shipFailures) {
          logger.error(logMessage, null, metadata);
        } else {
          logger.local.error(logMessage, null, metadata);
        }
        return { error: errorMessage, status: response.status };
      }

      let data: T;
      try {
        const responseData = await response.json();
        data = responseData as T;
      } catch (parseError: unknown) {
        let errorMessage = "Failed to parse response.";
        if (parseError instanceof Error) {
          errorMessage = parseError.message;
        } else if (typeof parseError === "string") {
          errorMessage = parseError;
        }
        const errorToLog = new Error(errorMessage);

        if (shipFailures) {
          logger.error("Parse failed", errorToLog);
        } else {
          logger.local.error("Parse failed", errorToLog);
        }
        return { error: errorMessage, status: 500 };
      }

      if (toastId && logToUI) {
        logger.updateToast(toastId, baseLogMessage, "success");
      }
      logger.devOnly.debug(
        `API Fetch Success: ${method} ${backendUrl}${requestEndpoint}`,
        { responseStatus: response.status },
      );
      return data;
    } catch (error: unknown) {
      let errorMessage = "An unknown network error occurred.";
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === "string") {
        errorMessage = error;
      }

      if (toastId && logToUI) {
        logger.updateToast(toastId, baseLogMessage, "failure", errorMessage);
      }
      const typedError =
        error instanceof Error ? error : new Error(String(error));
      const logMessage = `API Fetch Exception: ${method} ${backendUrl}${requestEndpoint}`;
      if (shipFailures) {
        logger.error(logMessage, typedError);
      } else {
        logger.local.error(logMessage, typedError);
      }
      return { error: errorMessage };
    }
  },
};

export default apiUtils;
